import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { LoadFailure } from '../../components/LoadFailure';
import { TableLoading } from '../../components/TableLoading';
import { useAuth } from '../../core/authContext';
import type { AuthSnapshot } from '../ProgressReports/students';
import { fetchImplementationReport, type ImplementationReportResponse, type ReportQuery, type UsageSchool } from './api';
import { Card, IMPLEMENTATION_ROUTE, outlinedButton, primaryButton, readyOf, Shell, TEACHER_COLUMNS, teacherRow, Unavailable, UsageTable, useReport, useReportQuery, type Ready } from './index';
import { localToday, rangeLabel } from './period';
import { previousSchoolTotals, schoolRows } from './rows';
import { USAGE } from './tokens';

export const printStyles = `@page { size: letter; margin: 0; }
@media print {
  [data-print="hide"], [data-testid="usage-print-bar"] { display: none !important; }
  [data-print="flat"] { padding: 0 !important; gap: 0 !important; max-width: none !important; }
  [data-testid="usage-print-pages"] { gap: 0 !important; }
  [data-testid="usage-print-page"] { box-shadow: none !important; page-break-after: always; break-after: page; }
}`;

function PrintStat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <Box>
      <Typography sx={{ color: USAGE.muted, fontFamily: USAGE.font, fontSize: 12, fontWeight: 700, lineHeight: '16px', letterSpacing: '1px', textTransform: 'uppercase' }}>{label}</Typography>
      <Typography sx={{ fontFamily: USAGE.font, fontSize: 32, fontWeight: 800, lineHeight: '40px', color: USAGE.ink }}>{value}</Typography>
      <Typography sx={{ color: USAGE.muted, fontFamily: USAGE.font, fontSize: 12, lineHeight: '16px' }}>{sub}</Typography>
    </Box>
  );
}

export function printPagesOf<T>(rows: T[], perPage: number = USAGE.printRowsPerPage): T[][] {
  if (rows.length === 0) return [[]];
  const pages: T[][] = [];
  for (let i = 0; i < rows.length; i += perPage) pages.push(rows.slice(i, i + perPage));
  return pages;
}

function SchoolPage({ ready, school, districtName }: { ready: Ready; school: UsageSchool; districtName: string }) {
  const previous = previousSchoolTotals(ready.previous, school.schoolId);
  const cur = school.totals;
  const rows = schoolRows(ready.current, school.schoolId).map((r) => teacherRow(r, r.name));
  const period = ready.data.period!;
  const pages = printPagesOf(rows);
  return (
    <>
      {pages.map((pageRows, index) => (
        <Box key={index} data-testid="usage-print-page" component="article" sx={{ width: 816, height: 1056, overflow: 'hidden', boxSizing: 'border-box', p: '48px', bgcolor: USAGE.cardBg, boxShadow: '0 2px 8px rgba(8, 8, 27, 0.12)', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Box>
            <Typography sx={{ color: USAGE.muted, fontFamily: USAGE.font, fontSize: 12, fontWeight: 700, lineHeight: '16px', letterSpacing: '1px', textTransform: 'uppercase' }}>{`${districtName} · Implementation report`}</Typography>
            <Typography component="h1" sx={{ my: '4px', fontFamily: USAGE.font, fontSize: 32, fontWeight: 800, lineHeight: '40px', letterSpacing: '-0.5px', color: USAGE.ink }}>{school.name}</Typography>
            <Typography sx={{ color: USAGE.muted, fontFamily: USAGE.font, fontSize: 16, lineHeight: '24px', pb: '16px', borderBottom: `2px solid ${USAGE.ink}` }}>
              {`${rangeLabel(period.start, period.end)} · compared with ${rangeLabel(ready.data.previous!.start, ready.data.previous!.end)}`}
              {pages.length > 1 ? ` · page ${index + 1} of ${pages.length}` : ''}
            </Typography>
          </Box>
          {index === 0 && (
            <Box sx={{ display: 'flex', gap: '32px' }}>
              <PrintStat label="Teachers active" value={`${cur.teachersActive} / ${cur.teachersRostered}`} sub={`was ${previous.teachersActive} / ${previous.teachersRostered}`} />
              <PrintStat label="Toolbox" value={String(cur.toolboxViews)} sub={`was ${previous.toolboxViews}`} />
              <PrintStat label="Students active" value={`${cur.studentsActive} / ${cur.studentsRostered}`} sub={`was ${previous.studentsActive} / ${previous.studentsRostered}`} />
              <PrintStat label="Academy started" value={`${cur.academyStarted} / ${cur.teachersRostered}`} sub={`${cur.academyJoined} joined · ${cur.academyFinished} finished`} />
            </Box>
          )}
          <Box>
            <Typography component="h2" sx={{ fontFamily: USAGE.font, fontSize: 20, fontWeight: 700, lineHeight: '28px', color: USAGE.ink, pb: '8px' }}>
              Teachers <Box component="small" sx={{ color: USAGE.muted, fontSize: 12, fontWeight: 400 }}>{index === 0 ? 'Alphabetical' : 'Continued'}</Box>
            </Typography>
            <UsageTable testId={`usage-print-table-${school.schoolId}`} columns={TEACHER_COLUMNS} rows={pageRows} compact />
          </Box>
          <Typography sx={{ color: USAGE.muted, fontFamily: USAGE.font, fontSize: 12, lineHeight: '16px', mt: 'auto' }}>
            Active = students who logged in at least once · Days = days with at least one login · Courses = Academy courses started · Finished = every step viewed · Assignments: see Assignment monitoring
          </Typography>
        </Box>
      ))}
    </>
  );
}

export function ImplementationReportPrintPage({
  source = fetchImplementationReport,
  print = () => window.print(),
  today = localToday(),
}: {
  source?: (auth: AuthSnapshot, query: ReportQuery) => Promise<ImplementationReportResponse>;
  print?: () => void;
  today?: string;
}) {
  const { isDistrictAdmin, isSuperAdmin } = useAuth();
  const [params] = useSearchParams();
  const query = useReportQuery();
  const schoolId = params.get('school') ?? undefined;
  const loaded = useReport(source, { granularity: query.granularity, start: query.start, today });
  const ready = useMemo(() => (loaded.kind === 'ready' ? readyOf(loaded.data, today) : null), [loaded, today]);
  if (!isDistrictAdmin && !isSuperAdmin) return <Navigate to="/reports" replace />;
  const back = schoolId ? `${IMPLEMENTATION_ROUTE}/school/${schoolId}${query.search}` : IMPLEMENTATION_ROUTE + query.search;
  const schools = ready ? ready.current.schools.filter((s) => !schoolId || s.schoolId === schoolId) : [];
  const missingSchool = !!ready && !!schoolId && schools.length === 0;
  return (
    <Shell crumbs={[{ label: 'Reports', to: '/reports' }, { label: 'Implementation report', to: IMPLEMENTATION_ROUTE + query.search }, { label: 'Print' }]}>
      <style>{printStyles}</style>
      <Box data-testid="usage-print-bar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
        <Box component={Link} to={back} sx={outlinedButton}>Back</Box>
        <Box component="button" type="button" onClick={print} sx={{ ...primaryButton, '&:disabled': { opacity: 0.5, cursor: 'default' } }} disabled={!ready || missingSchool}>Print</Box>
      </Box>
      {loaded.kind === 'loading' && <Card><TableLoading tokens={USAGE} rows={4} /></Card>}
      {loaded.kind === 'error' && <Card><LoadFailure tokens={USAGE} /></Card>}
      {loaded.kind === 'ready' && !ready && <Unavailable data={loaded.data} />}
      {missingSchool && <Card><Typography sx={{ fontFamily: USAGE.font, fontSize: 16, fontWeight: 700, color: USAGE.ink }}>This school is not in the report.</Typography></Card>}
      {ready && (
        <Box data-testid="usage-print-pages" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
          {schools.map((school) => <SchoolPage key={school.schoolId} ready={ready} school={school} districtName={ready.data.districtName ?? ''} />)}
        </Box>
      )}
    </Shell>
  );
}
